import type { InternalOrdersCopyProductAttachmentsResultItemSchema } from '@/api/schemas/orders/internal'
import type { OrderProductComponentResponseSchema, OrderProductResponseSchema } from '@/api/schemas/orders/orders'
import { createEmptyOrderCreateOrder } from '@/stores/orders/internalOrders/state'
import type {
    InternalOrderCreateComponentProcess,
    InternalOrderCreateComponentProperty,
    InternalOrderCreateDraft,
    InternalOrderCreateProduct,
    InternalOrderCreateProductComponent,
    InternalOrderCreateProductTemplate,
    InternalOrderCreateProductTemplateJson,
    InternalOrderCreateState,
    InternalOrderDetails,
    InternalOrderProductionFileAttachment,
    NumberConfigurationTypeItem
} from '@/types/domain/orders/internal'
import { normalizeProductDimension } from '@/views/Orders/InternalOrders/helpers/orderCreateRequestHelpers'

export interface BuildOrderCreateStateFromOrderOptions {
    copyAttachments: boolean
    copiedAttachments?: InternalOrdersCopyProductAttachmentsResultItemSchema[]
    numberConfigurationTypes: NumberConfigurationTypeItem[]
}

export function orderHasProductAttachments(order: InternalOrderDetails): boolean {
    return (order.orderProducts ?? []).some((product) => (product.orderProductAttachment?.length ?? 0) > 0)
}

export function buildOrderCreateStateFromOrder(
    sourceOrder: InternalOrderDetails,
    options: BuildOrderCreateStateFromOrderOptions
): InternalOrderCreateState {
    const sourceProducts = (sourceOrder.orderProducts ?? []) as OrderProductResponseSchema[]
    const attachmentsByProductId = indexCopiedAttachmentsByProductId(options.copiedAttachments)
    const { products, templates } = mapSourceProductsToCreatePayload(
        sourceProducts,
        attachmentsByProductId,
        options.copyAttachments
    )

    return {
        order: mapSourceOrderToCreateDraft(sourceOrder, products, options.numberConfigurationTypes),
        productTemplates: templates,
        deliveryFile: null,
        adviceNoteDraft: null
    }
}

function mapSourceOrderToCreateDraft(
    sourceOrder: InternalOrderDetails,
    orderProducts: InternalOrderCreateProduct[],
    numberConfigurationTypes: NumberConfigurationTypeItem[]
): InternalOrderCreateDraft {
    return {
        ...createEmptyOrderCreateOrder(),
        sourceOrderId: sourceOrder.id ?? null,
        name: sourceOrder.name ?? null,
        numberConfigurationTypeId: inferNumberConfigurationTypeId(sourceOrder.productionNumber, numberConfigurationTypes),
        customerId: sourceOrder.customer?.id ?? null,
        customer: sourceOrder.customer ?? null,
        ownerId: sourceOrder.ownerId ?? sourceOrder.owner?.id ?? null,
        netPrice: sourceOrder.netPrice != null ? Number(sourceOrder.netPrice) : 0,
        valuation: sourceOrder.valuation != null ? Number(sourceOrder.valuation) : 0,
        linkedToCampaign: sourceOrder.linkedToCampaign ?? false,
        orderProducts,
        plannedSendDate: sourceOrder.plannedSendDate ?? null,
        plannedDeliveryDate: sourceOrder.plannedDeliveryDate ?? null,
        receiveMaterialDate: sourceOrder.receiveMaterialDate ?? null,
        receiveFileDate: sourceOrder.receiveFileDate ?? null,
        acceptFileDate: sourceOrder.acceptFileDate ?? null,
        orderGraphicFileDeliveryMethodId: sourceOrder.orderGraphicFileDeliveryMethodId ?? null,
        description: sourceOrder.description ?? null,
        comments: [],
        orderDeliveryMethod: mapSourceDeliveryMethod(sourceOrder.orderDeliveryMethod),
        parameters: [],
        deliveries: []
    }
}

function inferNumberConfigurationTypeId(
    productionNumber: string | null | undefined,
    numberConfigurationTypes: NumberConfigurationTypeItem[]
): number | null {
    if (!productionNumber || numberConfigurationTypes.length === 0) {
        return null
    }

    const prefix = productionNumber.split('_')[0]
    return numberConfigurationTypes.find((type) => type.name === prefix)?.id ?? null
}

function mapSourceDeliveryMethod(
    deliveryMethod: InternalOrderDetails['orderDeliveryMethod']
): InternalOrderCreateDraft['orderDeliveryMethod'] {
    const methodId = deliveryMethod?.methodId ?? deliveryMethod?.deliveryMethodId ?? null

    return {
        methodId,
        deliveryMethodId: deliveryMethod?.deliveryMethodId ?? deliveryMethod?.methodId ?? null,
        cost: deliveryMethod?.cost ?? null,
        distance: deliveryMethod?.distance ?? null
    }
}

function indexCopiedAttachmentsByProductId(
    copiedAttachments: InternalOrdersCopyProductAttachmentsResultItemSchema[] | undefined
): Map<number, InternalOrderProductionFileAttachment[]> {
    return new Map((copiedAttachments ?? []).map((item) => [item.sourceOrderProductId, item.orderProductAttachments]))
}

function mapSourceProductsToCreatePayload(
    sourceProducts: OrderProductResponseSchema[],
    attachmentsByProductId: Map<number, InternalOrderProductionFileAttachment[]>,
    copyAttachments: boolean
): { products: InternalOrderCreateProduct[]; templates: InternalOrderCreateProductTemplate[] } {
    const products: InternalOrderCreateProduct[] = []
    const templates: InternalOrderCreateProductTemplate[] = []

    for (const sourceProduct of sourceProducts) {
        const productionFiles = resolveProductionFilesForProduct(sourceProduct, attachmentsByProductId, copyAttachments)
        const mapped = mapSourceProductToCreatePayload(sourceProduct, productionFiles)

        products.push(mapped.product)
        templates.push(mapped.template)
    }

    return { products, templates }
}

function resolveProductionFilesForProduct(
    sourceProduct: OrderProductResponseSchema,
    attachmentsByProductId: Map<number, InternalOrderProductionFileAttachment[]>,
    copyAttachments: boolean
): InternalOrderProductionFileAttachment[] {
    if (!copyAttachments || sourceProduct.id == null) {
        return []
    }

    return [...(attachmentsByProductId.get(sourceProduct.id) ?? [])]
}

function mapSourceProductToCreatePayload(
    sourceProduct: OrderProductResponseSchema,
    productionFiles: InternalOrderProductionFileAttachment[]
): { product: InternalOrderCreateProduct; template: InternalOrderCreateProductTemplate } {
    const orderProductComponents = (sourceProduct.orderProductComponents ?? []).map(mapSourceComponentToCreatePayload)
    const productTypeId = resolveProductTypeId(sourceProduct)
    const templateJson: InternalOrderCreateProductTemplateJson = {
        productionFileInPathId: sourceProduct.productionFileInPathId ?? null,
        productTypeId,
        formatA: normalizeProductDimension(sourceProduct.formatA),
        formatB: normalizeProductDimension(sourceProduct.formatB),
        orderProductComponents,
        description: sourceProduct.description ?? null
    }

    const product: InternalOrderCreateProduct = {
        name: sourceProduct.name,
        productTypeId,
        amount: sourceProduct.amount,
        description: sourceProduct.description ?? null,
        numberOfVersions: sourceProduct.numberOfVersions ?? 1,
        orderProductComponents,
        weight: sourceProduct.weightTotal ?? sourceProduct.weightSingle ?? 0,
        destinyPaperId: sourceProduct.destinyPaperId ?? null,
        productionFiles,
        orderProductAttachmentId: productionFiles.map((file) => file.id).filter((id): id is number => id > 0),
        formatA: normalizeProductDimension(sourceProduct.formatA),
        formatB: normalizeProductDimension(sourceProduct.formatB),
        productionFileInPathId: sourceProduct.productionFileInPathId ?? null,
        signalAmount: sourceProduct.signalAmount ?? 0
    }

    const template: InternalOrderCreateProductTemplate = {
        name: sourceProduct.name,
        tag: null,
        jsonTemplate: templateJson
    }

    return { product, template }
}

function resolveProductTypeId(sourceProduct: OrderProductResponseSchema): number | null {
    return sourceProduct.productTypeId ?? sourceProduct.productType?.id ?? null
}

function mapSourceComponentToCreatePayload(component: OrderProductComponentResponseSchema): InternalOrderCreateProductComponent {
    return {
        componentId: component.componentId,
        component: null,
        numberOfCopies: resolveNumberOfCopies(component),
        orderComponentProperties: (component.orderComponentProperties ?? []).map(mapSourceComponentPropertyToCreatePayload),
        orderComponentProcesses: (component.orderComponentProcesses ?? []).map(mapSourceComponentProcessToCreatePayload)
    }
}

function resolveNumberOfCopies(component: OrderProductComponentResponseSchema): number {
    const rawComponent = component as OrderProductComponentResponseSchema & { numberOfCopies?: number }
    const copies = rawComponent.numberOfCopies

    return typeof copies === 'number' && copies > 0 ? copies : 1
}

function mapSourceComponentPropertyToCreatePayload(property: unknown): InternalOrderCreateComponentProperty {
    const apiProperty = property as {
        productProductionPropertyId?: number
        productProductionProperty?: { id?: number }
        value?: string | number | null
    }

    return {
        productProductionPropertyId: apiProperty.productProductionPropertyId ?? apiProperty.productProductionProperty?.id,
        value: apiProperty.value ?? ''
    }
}

function mapSourceComponentProcessToCreatePayload(process: unknown): InternalOrderCreateComponentProcess {
    const apiProcess = process as {
        productionProcessId?: number
        productionProcess?: { id?: number }
    }

    return {
        productionProcessId: apiProcess.productionProcessId ?? apiProcess.productionProcess?.id
    }
}
